import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Clock, Users, Mail, MessageCircle, Settings, Trash2, Bell, 
  Calendar, Sun, Sunset, Moon, Zap, Plus, Search, Filter
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    morning_start: "06:00",
    morning_end: "12:00",
    midday_start: "12:00",
    midday_end: "17:00",
    evening_start: "17:00",
    evening_end: "21:00",
    notify_email: true,
    notify_whatsapp: true,
    email_template_id: null,
    whatsapp_template_name: null,
    allow_auto_booking: true,
    min_hours_before_auto_book: 2,
    booking_link_valid_hours: 24,
    // Verteilungs-Modus: "broadcast" (Default, alle Wartenden gleichzeitig)
    // oder "exclusive" (nur erster Wartender bekommt Mail, Eskalation nach
    // Fristablauf).
    distribution_mode: "broadcast",
    exclusive_window_minutes: 60,
  });
  
  // Waitlist entries
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('waiting'); // Standard: Nur wartende anzeigen
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('entries');
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [trainersLoaded, setTrainersLoaded] = useState(false);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  
  // Add to waitlist dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [newEntry, setNewEntry] = useState({
    customer_id: '',
    service_id: '',
    time_preferences: ['any'],
    custom_time_ranges: [],
    preferred_days: ['daily'],
    specific_date: null, // For single-day waitlist
    preferred_trainers: [], // Selected trainer IDs
    auto_book_enabled: false,
    auto_book_hours_before: 6
  });
  
  // Trainers list
  const [trainers, setTrainers] = useState([]);
  
  // Custom time range input
  const [customTimeStart, setCustomTimeStart] = useState('07:00');
  const [customTimeEnd, setCustomTimeEnd] = useState('10:00');
  
  // Date mode: 'recurring' (weekly) or 'specific' (single date)
  const [dateMode, setDateMode] = useState('recurring');
  const [specificDate, setSpecificDate] = useState('');
  
  // Weekday options
  const weekdayOptions = [
    { value: 'daily', label: 'Täglich' },
    { value: 'monday', label: 'Montag' },
    { value: 'tuesday', label: 'Dienstag' },
    { value: 'wednesday', label: 'Mittwoch' },
    { value: 'thursday', label: 'Donnerstag' },
    { value: 'friday', label: 'Freitag' },
    { value: 'saturday', label: 'Samstag' },
    { value: 'sunday', label: 'Sonntag' }
  ];
  
  // Customer search
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  // Filter customers based on search
  const filteredCustomers = customers.filter(c => {
    if (!customerSearchTerm) return true;
    const search = customerSearchTerm.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search)
    );
  });
  
  // Email/WhatsApp templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [waTemplates, setWaTemplates] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchEntries();
  }, []);

  useEffect(() => {
    if (activeTab === 'settings') {
      ensureTemplatesLoaded();
    }
  }, [activeTab]);

  useEffect(() => {
    if (showAddDialog) {
      ensureCustomersLoaded();
      ensureServicesLoaded();
      ensureTrainersLoaded();
    }
  }, [showAddDialog]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/waitlist/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/waitlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setEntriesLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoaded(true);
    }
  };

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setServicesLoaded(true);
    }
  };

  const fetchTrainers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only trainers/admins who can have appointments
      const allUsers = response.data || [];
      setTrainers(allUsers.filter(u => u.role === 'trainer' || u.role === 'admin'));
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setTrainersLoaded(true);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Email templates
      const emailRes = await axios.get(`${API}/email/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }));
      setEmailTemplates(emailRes.data || []);
      
      // WA templates
      const waRes = await axios.get(`${API}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { templates: [] } }));
      setWaTemplates(waRes.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setTemplatesLoaded(true);
    }
  };

  const ensureCustomersLoaded = () => {
    if (!customersLoaded) {
      fetchCustomers();
    }
  };

  const ensureServicesLoaded = () => {
    if (!servicesLoaded) {
      fetchServices();
    }
  };

  const ensureTrainersLoaded = () => {
    if (!trainersLoaded) {
      fetchTrainers();
    }
  };

  const ensureTemplatesLoaded = () => {
    if (!templatesLoaded) {
      fetchTemplates();
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/waitlist/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addToWaitlist = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/waitlist`, newEntry, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kunde zur Warteliste hinzugefügt');
      setShowAddDialog(false);
      setNewEntry({
        customer_id: '',
        service_id: '',
        time_preferences: ['any'],
        custom_time_ranges: [],
        preferred_days: ['daily'],
        specific_date: null,
        preferred_trainers: [],
        auto_book_enabled: false,
        auto_book_hours_before: 6
      });
      setCustomTimeStart('07:00');
      setCustomTimeEnd('10:00');
      setCustomerSearchTerm('');
      setShowCustomerDropdown(false);
      setDateMode('recurring');
      setSpecificDate('');
      fetchEntries();
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    }
  };

  const removeFromWaitlist = async (entryId) => {
    if (!window.confirm('Möchten Sie diesen Eintrag wirklich entfernen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/waitlist/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Von Warteliste entfernt');
      fetchEntries();
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      toast.error('Fehler beim Entfernen');
    }
  };

  const toggleTimePreference = (pref) => {
    setNewEntry(prev => {
      const current = prev.time_preferences || [];
      if (pref === 'any') {
        return { ...prev, time_preferences: ['any'] };
      }
      
      let updated = current.filter(p => p !== 'any');
      if (updated.includes(pref)) {
        updated = updated.filter(p => p !== pref);
      } else {
        updated.push(pref);
      }
      
      if (updated.length === 0) {
        updated = ['any'];
      }
      
      return { ...prev, time_preferences: updated };
    });
  };

  const filteredEntries = entries.filter(entry => {
    if (filterStatus !== 'all' && entry.status !== filterStatus) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        entry.customer_name?.toLowerCase().includes(search) ||
        entry.service_name?.toLowerCase().includes(search) ||
        entry.customer_email?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      waiting: { label: 'Wartend', color: 'bg-yellow-100 text-yellow-800' },
      notified: { label: 'Benachrichtigt', color: 'bg-blue-100 text-blue-800' },
      booked: { label: 'Gebucht', color: 'bg-green-100 text-green-800' },
      expired: { label: 'Abgelaufen', color: 'bg-gray-100 text-gray-800' },
      cancelled: { label: 'Storniert', color: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || statusConfig.waiting;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getTimePreferenceLabel = (prefs) => {
    if (!prefs || prefs.includes('any')) return 'Jederzeit';
    const labels = {
      morning: '🌅 Morgen',
      midday: '☀️ Mittag',
      evening: '🌙 Abend'
    };
    return prefs.map(p => labels[p] || p).join(', ');
  };

  if (loading) {
    return <div className="p-8 text-center">Laden...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Warteliste</h1>
          <p className="text-gray-500">Verwalte Wartelisten-Einträge und Benachrichtigungen</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="waitlist-add-customer-button">
          <Plus className="w-4 h-4" />
          Kunde hinzufügen
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4" data-testid="waitlist-tabs">
        <TabsList data-testid="waitlist-tabs-list">
          <TabsTrigger value="entries" className="gap-2" data-testid="waitlist-tab-entries">
            <Users className="w-4 h-4" />
            Warteliste ({entries.filter(e => e.status === 'waiting').length})
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2" data-testid="waitlist-tab-settings">
            <Settings className="w-4 h-4" />
            Einstellungen
          </TabsTrigger>
        </TabsList>

        {/* Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex gap-4 items-center">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Suche nach Kunde oder Service..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status filtern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="waiting">Wartend</SelectItem>
                    <SelectItem value="notified">Benachrichtigt</SelectItem>
                    <SelectItem value="booked">Gebucht</SelectItem>
                    <SelectItem value="expired">Abgelaufen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Entries Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Platz</TableHead>
                  <TableHead>Kunde</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Trainer</TableHead>
                  <TableHead>Zeitpräferenz</TableHead>
                  <TableHead>Auto-Buchung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Hinzugefügt</TableHead>
                  <TableHead>Von</TableHead>
                  <TableHead>Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                      Keine Einträge gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEntries.map((entry, index) => {
                    // Helper function to check if two time ranges overlap
                    const timeRangesOverlap = (ranges1, ranges2) => {
                      // If either has "any" time preference, they overlap
                      const prefs1 = entry.time_preferences || ['any'];
                      const prefs2 = ranges2.time_preferences || ['any'];
                      if (prefs1.includes('any') || prefs2.includes('any')) return true;
                      
                      // Check custom time ranges overlap
                      const custom1 = ranges1.custom_time_ranges || [];
                      const custom2 = ranges2.custom_time_ranges || [];
                      
                      if (custom1.length > 0 && custom2.length > 0) {
                        // Check if any custom ranges overlap
                        for (const r1 of custom1) {
                          const [start1, end1] = r1.split('-');
                          for (const r2 of custom2) {
                            const [start2, end2] = r2.split('-');
                            // Ranges overlap if start1 < end2 AND start2 < end1
                            if (start1 < end2 && start2 < end1) return true;
                          }
                        }
                        return false;
                      }
                      
                      // Check time_preferences overlap (morning/midday/evening)
                      return prefs1.some(p => prefs2.includes(p));
                    };
                    
                    // Calculate position based on overlapping time preferences (only for waiting entries)
                    const overlappingEntries = filteredEntries.filter(e => 
                      e.status === 'waiting' && 
                      e.id !== entry.id &&
                      timeRangesOverlap(entry, e)
                    );
                    
                    // Add current entry and sort by created_at
                    const allOverlapping = entry.status === 'waiting' 
                      ? [...overlappingEntries, entry].sort((a, b) => 
                          new Date(a.created_at) - new Date(b.created_at)
                        )
                      : [];
                    
                    const position = entry.status === 'waiting' 
                      ? allOverlapping.findIndex(e => e.id === entry.id) + 1 
                      : null;
                    const totalInQueue = allOverlapping.length;
                    
                    return (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {position ? (
                          <div className="flex items-center justify-center">
                            <span className={`
                              inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold
                              ${position === 1 ? 'bg-yellow-100 text-yellow-800 ring-2 ring-yellow-400' : 
                                position === 2 ? 'bg-gray-100 text-gray-700' : 
                                position === 3 ? 'bg-orange-100 text-orange-700' : 
                                'bg-gray-50 text-gray-600'}
                            `}>
                              {position}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-center block">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{entry.customer_name}</p>
                          <p className="text-xs text-gray-500">{entry.customer_email}</p>
                          {totalInQueue > 1 && position && (
                            <p className="text-xs text-blue-600 mt-1">
                              Platz {position} von {totalInQueue} (gleicher Zeitslot)
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{entry.service_name}</TableCell>
                      <TableCell>
                        {entry.preferred_trainers && entry.preferred_trainers.length > 0 ? (
                          <div className="text-xs text-purple-600">
                            {entry.preferred_trainers.map(tid => {
                              const trainer = trainers.find(t => t.id === tid);
                              return trainer ? `${trainer.first_name}` : tid;
                            }).join(', ')}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">Alle</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div>{getTimePreferenceLabel(entry.time_preferences)}</div>
                          {entry.custom_time_ranges && entry.custom_time_ranges.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {entry.custom_time_ranges.map((range, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                  🕐 {range}
                                </Badge>
                              ))}
                            </div>
                          )}
                          {entry.specific_date && (
                            <div className="text-xs text-purple-600 font-medium">
                              📌 Nur am {new Date(entry.specific_date).toLocaleDateString('de-CH', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </div>
                          )}
                          {!entry.specific_date && entry.preferred_days && !entry.preferred_days.includes('daily') && (
                            <div className="text-xs text-green-600">
                              🔄 {entry.preferred_days.map(d => {
                                const days = {monday: 'Mo', tuesday: 'Di', wednesday: 'Mi', thursday: 'Do', friday: 'Fr', saturday: 'Sa', sunday: 'So'};
                                return days[d] || d;
                              }).join(', ')}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {entry.auto_book_enabled ? (
                          <Badge className="bg-green-100 text-green-800">
                            <Zap className="w-3 h-3 mr-1" />
                            {entry.auto_book_hours_before}h vorher
                          </Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(entry.status)}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(entry.created_at).toLocaleDateString('de-CH')}
                      </TableCell>
                      <TableCell>
                        {entry.created_by === 'self' ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            🙋 Selbst
                          </Badge>
                        ) : entry.created_by?.startsWith('admin') ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            👑 Admin
                          </Badge>
                        ) : entry.created_by?.startsWith('trainer') ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            🏋️ Trainer
                          </Badge>
                        ) : entry.created_by && entry.created_by !== 'None' && entry.created_by !== 'null' ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            👤 Team
                          </Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromWaitlist(entry.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )})
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Allgemeine Einstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Warteliste aktiviert</Label>
                  <p className="text-sm text-gray-500">Kunden können sich selbst zur Warteliste hinzufügen</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-Buchung erlauben</Label>
                  <p className="text-sm text-gray-500">Kunden können automatische Buchung bei freien Slots aktivieren</p>
                </div>
                <Switch
                  checked={settings.allow_auto_booking}
                  onCheckedChange={(checked) => setSettings({ ...settings, allow_auto_booking: checked })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Min. Stunden für Auto-Buchung</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={settings.min_hours_before_auto_book}
                    onChange={(e) => setSettings({ ...settings, min_hours_before_auto_book: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Buchungslink Gültigkeit (Stunden)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="72"
                    value={settings.booking_link_valid_hours}
                    onChange={(e) => setSettings({ ...settings, booking_link_valid_hours: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              {/* Verteilungs-Modus für Slot-Benachrichtigung */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                <div>
                  <Label className="text-base font-semibold">Verteilungs-Modus</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Bestimmt, wie freie Termine an Wartende vergeben werden.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      settings.distribution_mode === 'broadcast'
                        ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="distribution-mode-broadcast"
                  >
                    <input
                      type="radio"
                      name="distribution_mode"
                      value="broadcast"
                      checked={settings.distribution_mode === 'broadcast'}
                      onChange={() => setSettings({ ...settings, distribution_mode: 'broadcast' })}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900">📣 Broadcast (Standard)</div>
                    <div className="text-sm text-gray-600 mt-2">
                      Alle passenden Wartenden bekommen <strong>gleichzeitig</strong> Email + WhatsApp.
                      Wer zuerst bucht, gewinnt (First-Come-First-Served).
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      ✓ Schnellste Slot-Vergabe<br/>
                      ✗ Stress für Kunden (Konkurrenz)
                    </div>
                  </label>
                  <label
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      settings.distribution_mode === 'exclusive'
                        ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="distribution-mode-exclusive"
                  >
                    <input
                      type="radio"
                      name="distribution_mode"
                      value="exclusive"
                      checked={settings.distribution_mode === 'exclusive'}
                      onChange={() => setSettings({ ...settings, distribution_mode: 'exclusive' })}
                      className="sr-only"
                    />
                    <div className="font-semibold text-gray-900">🎯 Exklusiv (Nacheinander)</div>
                    <div className="text-sm text-gray-600 mt-2">
                      Nur der <strong>erste</strong> Wartende bekommt das Angebot mit einer Frist.
                      Wenn er nicht reagiert, geht der Slot automatisch an den nächsten.
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      ✓ Stress-frei für Kunden<br/>
                      ✗ Slot kann länger frei stehen
                    </div>
                  </label>
                </div>
                {settings.distribution_mode === 'exclusive' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <Label className="text-sm font-semibold text-amber-900">
                      Frist pro Wartendem (Minuten)
                    </Label>
                    <Input
                      type="number"
                      min="5"
                      max="180"
                      value={settings.exclusive_window_minutes}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          exclusive_window_minutes: parseInt(e.target.value) || 60,
                        })
                      }
                      data-testid="exclusive-window-minutes-input"
                      className="mt-2 max-w-xs"
                    />
                    <p className="text-xs text-amber-800 mt-2">
                      Wird automatisch reduziert wenn der Slot näher liegt (mind. 5 Min Puffer vor Termin).
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Time Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Zeitfenster
              </CardTitle>
              <CardDescription>Definiere die Zeiträume für Morgen, Mittag und Abend</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Sun className="w-4 h-4 text-yellow-500" />
                    Morgen
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={settings.morning_start}
                      onChange={(e) => setSettings({ ...settings, morning_start: e.target.value })}
                    />
                    <span>–</span>
                    <Input
                      type="time"
                      value={settings.morning_end}
                      onChange={(e) => setSettings({ ...settings, morning_end: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Sunset className="w-4 h-4 text-orange-500" />
                    Mittag
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={settings.midday_start}
                      onChange={(e) => setSettings({ ...settings, midday_start: e.target.value })}
                    />
                    <span>–</span>
                    <Input
                      type="time"
                      value={settings.midday_end}
                      onChange={(e) => setSettings({ ...settings, midday_end: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-blue-500" />
                    Abend
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="time"
                      value={settings.evening_start}
                      onChange={(e) => setSettings({ ...settings, evening_start: e.target.value })}
                    />
                    <span>–</span>
                    <Input
                      type="time"
                      value={settings.evening_end}
                      onChange={(e) => setSettings({ ...settings, evening_end: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notification Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Benachrichtigungen
              </CardTitle>
              <CardDescription>Konfiguriere E-Mail und WhatsApp Benachrichtigungen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                {/* Email */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      E-Mail Benachrichtigung
                    </Label>
                    <Switch
                      checked={settings.notify_email}
                      onCheckedChange={(checked) => setSettings({ ...settings, notify_email: checked })}
                    />
                  </div>
                  {settings.notify_email && (
                    <div>
                      <Label className="text-sm">E-Mail Vorlage</Label>
                      <Select
                        value={settings.email_template_id || 'default'}
                        onValueChange={(val) => setSettings({ ...settings, email_template_id: val === 'default' ? null : val })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Standard-Vorlage" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Standard-Vorlage</SelectItem>
                          {emailTemplates.map(tpl => (
                            <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* WhatsApp */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      WhatsApp Benachrichtigung
                    </Label>
                    <Switch
                      checked={settings.notify_whatsapp}
                      onCheckedChange={(checked) => setSettings({ ...settings, notify_whatsapp: checked })}
                    />
                  </div>
                  {settings.notify_whatsapp && (
                    <div>
                      <Label className="text-sm">WhatsApp Template Name</Label>
                      <Input
                        placeholder="warteliste_slot"
                        value={settings.whatsapp_template_name || ''}
                        onChange={(e) => setSettings({ ...settings, whatsapp_template_name: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Template muss bei Meta Business erstellt werden
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? 'Speichern...' : 'Einstellungen speichern'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add to Waitlist Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setCustomerSearchTerm('');
          setShowCustomerDropdown(false);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onClick={() => setShowCustomerDropdown(false)}>
          <DialogHeader>
            <DialogTitle>Kunde zur Warteliste hinzufügen</DialogTitle>
            <DialogDescription>
              Wähle einen Kunden und Service für die Warteliste
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <Label>Kunde *</Label>
              <div className="relative mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Kunde suchen (Name, E-Mail, Telefon)..."
                    value={customerSearchTerm}
                    onChange={(e) => {
                      setCustomerSearchTerm(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="pl-10"
                  />
                </div>
                {showCustomerDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="p-3 text-gray-500 text-center text-sm">
                        Kein Kunde gefunden
                      </div>
                    ) : (
                      filteredCustomers.slice(0, 50).map(c => (
                        <div
                          key={c.id}
                          className={`p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${
                            newEntry.customer_id === c.id ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => {
                            setNewEntry({ ...newEntry, customer_id: c.id });
                            setCustomerSearchTerm(`${c.first_name} ${c.last_name}`);
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <div className="font-medium">{c.first_name} {c.last_name}</div>
                          <div className="text-xs text-gray-500">{c.email} {c.phone && `• ${c.phone}`}</div>
                        </div>
                      ))
                    )}
                    {filteredCustomers.length > 50 && (
                      <div className="p-2 text-center text-xs text-gray-400 bg-gray-50">
                        +{filteredCustomers.length - 50} weitere Ergebnisse - Suche verfeinern
                      </div>
                    )}
                  </div>
                )}
              </div>
              {newEntry.customer_id && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant="secondary" className="gap-1">
                    ✓ {customers.find(c => c.id === newEntry.customer_id)?.first_name} {customers.find(c => c.id === newEntry.customer_id)?.last_name}
                    <button
                      type="button"
                      onClick={() => {
                        setNewEntry({ ...newEntry, customer_id: '' });
                        setCustomerSearchTerm('');
                      }}
                      className="ml-1 hover:text-red-500"
                    >
                      ×
                    </button>
                  </Badge>
                </div>
              )}
            </div>

            <div>
              <Label>Service *</Label>
              <Select
                value={newEntry.service_id}
                onValueChange={(val) => setNewEntry({ ...newEntry, service_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Service wählen" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Trainer Auswahl */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Label className="text-purple-700 font-medium">👤 Bevorzugter Trainer (optional)</Label>
              <p className="text-xs text-purple-600 mb-2">Leer lassen = alle Trainer berücksichtigen</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {trainers.map(trainer => (
                  <Button
                    key={trainer.id}
                    type="button"
                    variant={newEntry.preferred_trainers.includes(trainer.id) ? 'default' : 'outline'}
                    size="sm"
                    className={newEntry.preferred_trainers.includes(trainer.id) ? 'bg-purple-600 hover:bg-purple-700' : ''}
                    onClick={() => {
                      const currentTrainers = newEntry.preferred_trainers || [];
                      if (currentTrainers.includes(trainer.id)) {
                        setNewEntry({
                          ...newEntry,
                          preferred_trainers: currentTrainers.filter(t => t !== trainer.id)
                        });
                      } else {
                        setNewEntry({
                          ...newEntry,
                          preferred_trainers: [...currentTrainers, trainer.id]
                        });
                      }
                    }}
                  >
                    {trainer.first_name} {trainer.last_name}
                  </Button>
                ))}
                {trainers.length === 0 && (
                  <p className="text-sm text-gray-500">Keine Trainer gefunden</p>
                )}
              </div>
            </div>

            {/* ======= ZEITAUSWAHL - VERBESSERT ======= */}
            <div className="p-4 bg-gray-50 rounded-lg border space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">⏰ Wann passt es?</Label>
                {newEntry.custom_time_ranges.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    Eigene Zeiten aktiv
                  </Badge>
                )}
              </div>

              {/* Option A: Eigenes Zeitfenster (hat Priorität) */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-blue-700 font-medium">🕐 Eigenes Zeitfenster</Label>
                <p className="text-xs text-blue-600 mb-2">Wenn gesetzt, wird nur in diesem Zeitraum benachrichtigt</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={customTimeStart}
                    onChange={(e) => setCustomTimeStart(e.target.value)}
                    className="w-28"
                  />
                  <span className="text-gray-500">bis</span>
                  <Input
                    type="time"
                    value={customTimeEnd}
                    onChange={(e) => setCustomTimeEnd(e.target.value)}
                    className="w-28"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const range = `${customTimeStart}-${customTimeEnd}`;
                      if (!newEntry.custom_time_ranges.includes(range)) {
                        setNewEntry({
                          ...newEntry,
                          custom_time_ranges: [...newEntry.custom_time_ranges, range],
                          time_preferences: [] // Deaktiviere "Jederzeit" etc.
                        });
                      }
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {newEntry.custom_time_ranges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newEntry.custom_time_ranges.map((range, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1 bg-blue-100 text-blue-700">
                        {range}
                        <button
                          type="button"
                          onClick={() => {
                            const newRanges = newEntry.custom_time_ranges.filter((_, i) => i !== idx);
                            setNewEntry({
                              ...newEntry,
                              custom_time_ranges: newRanges,
                              time_preferences: newRanges.length === 0 ? ['any'] : []
                            });
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Option B: Vordefinierte Tageszeiten (nur wenn kein eigenes Zeitfenster) */}
              {newEntry.custom_time_ranges.length === 0 && (
                <div>
                  <Label className="text-gray-600">Oder: Vordefinierte Tageszeit</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button
                      type="button"
                      variant={newEntry.time_preferences.includes('any') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleTimePreference('any')}
                    >
                      Jederzeit
                    </Button>
                    <Button
                      type="button"
                      variant={newEntry.time_preferences.includes('morning') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleTimePreference('morning')}
                    >
                      🌅 Morgen
                    </Button>
                    <Button
                      type="button"
                      variant={newEntry.time_preferences.includes('midday') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleTimePreference('midday')}
                    >
                      ☀️ Mittag
                    </Button>
                    <Button
                      type="button"
                      variant={newEntry.time_preferences.includes('evening') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleTimePreference('evening')}
                    >
                      🌙 Abend
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* ======= DATUM-AUSWAHL - NEU ======= */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
              <Label className="text-base font-semibold text-green-700">📅 Welche Tage?</Label>
              
              {/* Modus-Auswahl */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={dateMode === 'recurring' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setDateMode('recurring');
                    setNewEntry({ ...newEntry, specific_date: null });
                  }}
                  className={dateMode === 'recurring' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  🔄 Wiederkehrend
                </Button>
                <Button
                  type="button"
                  variant={dateMode === 'specific' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setDateMode('specific');
                    setNewEntry({ ...newEntry, preferred_days: [] });
                  }}
                  className={dateMode === 'specific' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  📌 Nur 1 Tag
                </Button>
              </div>

              {/* Wiederkehrend: Wochentage */}
              {dateMode === 'recurring' && (
                <div>
                  <p className="text-xs text-green-600 mb-2">Wähle die Wochentage für die Benachrichtigung</p>
                  <div className="flex flex-wrap gap-2">
                    {weekdayOptions.map(day => (
                      <Button
                        key={day.value}
                        type="button"
                        variant={newEntry.preferred_days.includes(day.value) ? 'default' : 'outline'}
                        size="sm"
                        className={newEntry.preferred_days.includes(day.value) ? 'bg-green-600 hover:bg-green-700' : ''}
                        onClick={() => {
                          if (day.value === 'daily') {
                            setNewEntry({ ...newEntry, preferred_days: ['daily'] });
                          } else {
                            let newDays = newEntry.preferred_days.filter(d => d !== 'daily');
                            if (newDays.includes(day.value)) {
                              newDays = newDays.filter(d => d !== day.value);
                            } else {
                              newDays.push(day.value);
                            }
                            if (newDays.length === 0) newDays = ['daily'];
                            setNewEntry({ ...newEntry, preferred_days: newDays });
                          }
                        }}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Spezifisches Datum */}
              {dateMode === 'specific' && (
                <div>
                  <p className="text-xs text-green-600 mb-2">Wähle ein bestimmtes Datum</p>
                  <Input
                    type="date"
                    value={specificDate}
                    onChange={(e) => {
                      setSpecificDate(e.target.value);
                      setNewEntry({ ...newEntry, specific_date: e.target.value });
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-48"
                  />
                  {specificDate && (
                    <p className="text-sm text-green-700 mt-2">
                      📌 Nur für: {new Date(specificDate).toLocaleDateString('de-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label>Automatisch buchen</Label>
                <p className="text-xs text-gray-500">
                  {newEntry.auto_book_enabled
                    ? `Wir buchen automatisch, wenn ein Termin in den nächsten ${newEntry.auto_book_hours_before} Stunden frei wird.`
                    : `Wir senden eine E-Mail mit Ein-Klick-Bestätigung, wenn ein Termin in den nächsten ${newEntry.auto_book_hours_before} Stunden frei wird.`}
                </p>
              </div>
              <Switch
                checked={newEntry.auto_book_enabled}
                onCheckedChange={(checked) => setNewEntry({ ...newEntry, auto_book_enabled: checked })}
                data-testid="admin-waitlist-autobook-switch"
              />
            </div>

            {/* Vorlauf-Picker — gilt für beide Varianten (Mail + Auto-Buchen) */}
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-gray-600">
                Vorlauf
              </Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {[3, 6, 12, 24].map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setNewEntry({ ...newEntry, auto_book_hours_before: h })}
                    data-testid={`admin-waitlist-vorlauf-${h}h`}
                    className={`h-10 rounded-lg text-sm font-bold transition-colors border ${
                      newEntry.auto_book_hours_before === h
                        ? 'bg-yellow-400 text-black border-yellow-400'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-2 leading-snug">
                Der Kunde wird (oder das System bucht) nur über Termine
                informiert, die innerhalb dieser Zeitspanne frei werden.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={addToWaitlist}
              disabled={!newEntry.customer_id || !newEntry.service_id}
            >
              Hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaitlistSettingsPage;
